// Story ▸ Shape (ShapeViews.swift): the manuscript scene by scene, from the first page at the left to the last at the
// right, each scene as wide as its words, every chart on that one axis so a column is the same scene in each: length
// and dialogue as bars, sentence length and vocabulary as lines, the point of view as a strip in each character's
// colour, the book's averages dashed, faint lines where chapters begin. Tap a chart to choose a scene: it's marked in
// every chart, with its figures above and Open in Write. Worth a look lists the scenes that differ most from the scenes
// around them; Export CSV saves every scene's figures. Described, never graded.
import { useRef, useState } from 'react';
import { entityCSSColor, type Project } from '@writing-suite/core';
import { shapeTotalWords, unwritten, type SceneShape, type ShapeScene } from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { downloadText, fileName, opened, shapeCSVOf, shapeOf } from '../../store/story.ts';
import { invert, scale, useWidth } from './charts.ts';
import { plural, StoryEmpty, useStoryEmphasis } from './common.tsx';

const one = (x: number) => x.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const two = (x: number) => x.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percent = (x: number) => `${Math.round(x * 100)}%`;

/** Export CSV…, in the page's header. */
export function ShapeControls({ project }: PageProps) {
  const shape = shapeOf(project);
  if (!shape?.scenes.length) return null;
  return (
    <button type="button" className="quiet-button" title="Every scene’s figures, for a spreadsheet" onClick={() => exportShape(project, shape)}>
      Export CSV…
    </button>
  );
}

function exportShape(project: Project, shape: SceneShape) {
  downloadText(fileName(`${project.title || 'Book'} scenes`, '.csv'), shapeCSVOf(project, shape));
}

export function ShapePage({ project }: PageProps) {
  const ws = useWorkspace();
  const shape = shapeOf(project);
  const [selected, setSelected] = useState<string | null>(null);
  const head = (
    <header className="page-head">
      <h1 tabIndex={-1}>Shape</h1>
      {shape?.scenes.length ? <p className="page-sub">{plural(shape.scenes.length, 'scene')} · {plural(shapeTotalWords(shape), 'word')}</p> : null}
    </header>
  );
  if (!shape) {
    return (
      <div className="story-page shape-page">
        {head}
        <StoryEmpty>Shape reads the prose of a manuscript. A screenplay’s scenes are measured in pages instead: see Write’s footer.</StoryEmpty>
      </div>
    );
  }
  if (!shape.scenes.some((s) => s.words > 0)) {
    return (
      <div className="story-page shape-page">
        {head}
        <StoryEmpty>Write some scenes in the manuscript to see how the book moves from one to the next.</StoryEmpty>
      </div>
    );
  }
  const chosen = shape.scenes.find((s) => s.id === selected) ?? null;
  const titles = new Map(shape.scenes.map((s) => [s.id, s.title]));
  const order: string[] = [];
  for (const n of shape.notable) if (!order.includes(n.sceneID)) order.push(n.sceneID);
  const blank = unwritten(shape).map((id) => titles.get(id) ?? 'Untitled');
  return (
    <div className="story-page shape-page" data-selected={selected ?? ''}>
      {head}
      {chosen ? (
        <div className="selected-scene-bar shape-selected" role="status" data-scene-id={chosen.id}>
          <div>
            <p className="selected-scene-title">{chosen.title}</p>
            <p className="selected-scene-time">
              {[
                chosen.chapter,
                plural(chosen.words, 'word'),
                chosen.sentences >= 5 ? `${one(chosen.sentenceLength)} words a sentence` : null,
                `${percent(chosen.dialogue)} dialogue`,
                chosen.vocabulary !== null ? `vocabulary ${two(chosen.vocabulary)}` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button type="button" className="button primary small" onClick={() => ws?.reveal({ fragmentId: chosen.id })}>
            Open in Write
          </button>
          <button type="button" className="quiet-button" aria-label="Clear" onClick={() => setSelected(null)}>
            ×
          </button>
        </div>
      ) : null}
      <ShapeCharts project={project} shape={shape} selected={chosen} onPick={(id) => setSelected(id === selected ? null : id)} />
      <div className="shape-notes">
        <p className="chart-note">
          Short sentences and dialogue usually read quickly, long sentences and narration slowly; a varied vocabulary often means description or reflection. These describe the
          book, they don’t grade it: a slow scene may be exactly right.
        </p>
        {order.length ? (
          <section aria-labelledby="worth-a-look">
            <h2 id="worth-a-look" className="eyebrow">Worth a look</h2>
            <ul className="worth-a-look">
              {order.map((id) => (
                <li key={id} data-scene-id={id}>
                  <div>
                    <p className="worth-title">{titles.get(id) ?? 'Untitled'}</p>
                    {shape.notable.filter((n) => n.sceneID === id).map((n) => (
                      <p key={n.id} className="worth-message">{n.message}</p>
                    ))}
                  </div>
                  <button type="button" className="quiet-button" onClick={() => setSelected(id)}>
                    Show
                  </button>
                  <button type="button" className="quiet-button" onClick={() => ws?.reveal({ fragmentId: id })}>
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {blank.length ? (
          <p className="chart-note">
            Not written yet (left out of the averages’ comparisons): {blank.slice(0, 6).join(', ')}
            {blank.length > 6 ? ` and ${blank.length - 6} more` : ''}.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ShapeCharts({ project, shape, selected, onPick }: { project: Project; shape: SceneShape; selected: ShapeScene | null; onPick: (id: string) => void }) {
  const { faded } = useStoryEmphasis();
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const total = Math.max(1, shapeTotalWords(shape));
  const gutter = width < 520 ? 70 : 96;
  const x0 = gutter, x1 = Math.max(x0 + 40, width - 8);
  const x = scale(0, total, x0, x1);
  const at = invert(0, total, x0, x1);
  const mid = (s: ShapeScene) => s.start + s.words / 2;
  const chapters: number[] = [];
  let last: string | null | undefined;
  for (const s of shape.scenes) {
    if (s.chapter !== last) {
      if (last !== undefined) chapters.push(s.start);
      last = s.chapter;
    }
  }
  const pick = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const pos = at(e.clientX - r.left);
    const hit = shape.scenes.find((s) => s.start <= pos && pos < s.start + s.words) ?? shape.scenes[shape.scenes.length - 1];
    if (hit) onPick(hit.id);
  };
  const keys = (e: React.KeyboardEvent) => {
    const i = selected ? shape.scenes.indexOf(selected) : -1;
    const next = e.key === 'ArrowRight' ? shape.scenes[Math.min(shape.scenes.length - 1, i + 1)] : e.key === 'ArrowLeft' ? shape.scenes[Math.max(0, i - 1)] : null;
    if (next) {
      e.preventDefault();
      if (next.id !== selected?.id) onPick(next.id);
    }
  };
  const chart = (name: string, height: number, labels: [number, string][], y: (v: number) => number, body: React.ReactNode, rules = true) => (
    <svg className={`story-chart shape-chart shape-${name}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="group" aria-label={`${name}: choose a scene with the pointer or the arrow keys`} tabIndex={0} onPointerUp={pick} onKeyDown={keys}>
      {rules ? chapters.map((c) => <line key={c} className="chapter-rule" x1={x(c)} x2={x(c)} y1={0} y2={height} />) : null}
      {body}
      {selected ? <line className="selected-rule" x1={x(mid(selected))} x2={x(mid(selected))} y1={0} y2={height} /> : null}
      {labels.map(([v, text], i) => (
        <text key={i} className="gutter-label" x={gutter - 8} y={Math.min(height - 3, Math.max(10, y(v) + 4))} textAnchor="end">
          {text}
        </text>
      ))}
    </svg>
  );
  const section = (title: string, note: string | null, content: React.ReactNode) => (
    <section className="shape-section" aria-label={title}>
      <p className="shape-section-head">
        <span className="eyebrow">{title}</span>
        {note ? <span className="chart-note inline">{note}</span> : null}
      </p>
      {content}
    </section>
  );
  const entities = opened(project).record.entities;

  // Length
  const topWords = Math.max(1, ...shape.scenes.map((s) => s.words));
  const LH = 130;
  const yLen = scale(0, topWords, LH, 4);
  const lengths = chart('length', LH, [[topWords, topWords.toLocaleString('en-GB')], [shape.averages.words, `avg ${Math.round(shape.averages.words).toLocaleString('en-GB')}`]], yLen, (
    <>
      {shape.scenes.map((s) => (
        <rect key={s.id} className={`shape-bar${s.id === selected?.id ? ' selected' : ''}${faded(s.id)}`} data-scene-id={s.id} x={x(s.start)} width={Math.max(0.5, x(s.start + s.words) - x(s.start))} y={yLen(s.words)} height={LH - yLen(s.words)} />
      ))}
      <line className="average" x1={x0} x2={x1} y1={yLen(shape.averages.words)} y2={yLen(shape.averages.words)} />
    </>
  ));

  // Point of view
  const povs = new Set(shape.scenes.map((s) => s.pov).filter(Boolean));
  const pov = chart('pov', 18, [[0.5, povs.size ? plural(povs.size, 'narrator') : 'none set']], () => 9, (
    <>
      {shape.scenes.map((s) => {
        const e = s.pov ? entities.find((x) => x.id === s.pov) : null;
        return <rect key={s.id} className={(e ? 'pov-cell' : 'pov-cell none') + faded(s.id)} data-scene-id={s.id} x={x(s.start)} width={Math.max(0.5, x(s.start + s.words) - x(s.start))} y={0} height={18} fill={e ? entityCSSColor(e.color) : undefined}><title>{e?.name ?? 'No point of view'}</title></rect>;
      })}
    </>
  ), false);

  // Sentence length
  const sentencePoints = shape.scenes.filter((s) => s.sentences >= 5);
  const topSentence = Math.max(10, ...sentencePoints.map((s) => s.sentenceLength)) * 1.1;
  const SH = 120;
  const ySen = scale(0, topSentence, SH - 4, 4);
  const sentences = chart('sentences', SH, [[topSentence / 1.1, `${Math.round(topSentence / 1.1)} words`], [shape.averages.sentenceLength, `avg ${one(shape.averages.sentenceLength)}`]], ySen, (
    <>
      <polyline className="shape-line" fill="none" points={sentencePoints.map((s) => `${x(mid(s))},${ySen(s.sentenceLength)}`).join(' ')} />
      {sentencePoints.map((s) => <circle key={s.id} className={`shape-point${s.id === selected?.id ? ' selected' : ''}${faded(s.id)}`} data-scene-id={s.id} cx={x(mid(s))} cy={ySen(s.sentenceLength)} r={s.id === selected?.id ? 4 : 2} />)}
      <line className="average" x1={x0} x2={x1} y1={ySen(shape.averages.sentenceLength)} y2={ySen(shape.averages.sentenceLength)} />
    </>
  ));

  // Dialogue
  const DH = 100;
  const yDia = scale(0, 1, DH, 4);
  const dialogue = chart('dialogue', DH, [[1, '100%'], [shape.averages.dialogue, `avg ${percent(shape.averages.dialogue)}`]], yDia, (
    <>
      {shape.scenes.filter((s) => s.words >= 50).map((s) => (
        <rect key={s.id} className={`dialogue-bar${s.id === selected?.id ? ' selected' : ''}${faded(s.id)}`} data-scene-id={s.id} x={x(s.start)} width={Math.max(0.5, x(s.start + s.words) - x(s.start))} y={yDia(s.dialogue)} height={DH - yDia(s.dialogue)} />
      ))}
      <line className="average" x1={x0} x2={x1} y1={yDia(shape.averages.dialogue)} y2={yDia(shape.averages.dialogue)} />
    </>
  ));

  // Vocabulary
  const vocab = shape.scenes.filter((s) => s.vocabulary !== null);
  const values = vocab.map((s) => s.vocabulary!);
  const low = Math.max(0, (values.length ? Math.min(...values) : 0.5) - 0.03);
  const high = Math.max(Math.min(1, (values.length ? Math.max(...values) : 0.9) + 0.03), low + 0.05);
  const VH = 110;
  const yVoc = scale(low, high, VH - 4, 4);
  const vocabulary = chart('vocabulary', VH, [[high, 'more varied'], [low, 'plainer']], yVoc, (
    <>
      <polyline className="shape-line" fill="none" points={vocab.map((s) => `${x(mid(s))},${yVoc(s.vocabulary!)}`).join(' ')} />
      {vocab.map((s) => <circle key={s.id} className={`shape-point${s.id === selected?.id ? ' selected' : ''}${faded(s.id)}`} data-scene-id={s.id} cx={x(mid(s))} cy={yVoc(s.vocabulary!)} r={s.id === selected?.id ? 4 : 2} />)}
      {shape.averages.vocabulary !== null ? <line className="average" x1={x0} x2={x1} y1={yVoc(shape.averages.vocabulary)} y2={yVoc(shape.averages.vocabulary)} /> : null}
    </>
  ));

  return (
    <div className="shape-charts" ref={box}>
      {section('Length', 'Words in each scene. The dashed line is the book’s average.', lengths)}
      {section('Point of view', null, pov)}
      {section('Sentence length', 'Average words per sentence. Lower usually reads faster.', sentences)}
      {section('Dialogue', 'The share of each scene inside quotation marks.', dialogue)}
      {section('Vocabulary', 'How varied the words are, in 100-word stretches. Higher is more varied.', vocabulary)}
      <p className="shape-axis" aria-hidden="true">
        <span>Start</span>
        <span>{plural(shapeTotalWords(shape), 'word')}</span>
      </p>
    </div>
  );
}
